import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../utils/api';
import './OTPVerification.css';

function OTPVerification({ email, temporaryToken, onVerificationSuccess, onResendOTP }) {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const [canResend, setCanResend] = useState(false);
  const otpInputs = useRef([]);

  // Timer countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    const newOtp = [...otp];
    for (let i = 0; i < digits.length; i++) {
      newOtp[i] = digits[i];
    }
    setOtp(newOtp);

    // Focus the last filled input or the next empty one
    const nextIndex = Math.min(digits.length, 5);
    otpInputs.current[nextIndex]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpCode = otp.join('');

    if (otpCode.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }

    setIsLoading(true);
    try {
      // Set temporary token for OTP verification
      api.setToken(temporaryToken);

      const response = await api.post('/api/auth/verify-otp', {
        email,
        otp_code: otpCode
      });

      if (response.access_token) {
        // Update token to the final access token
        api.setToken(response.access_token);
        localStorage.setItem('token', response.access_token);
        
        toast.success('Successfully verified!');
        onVerificationSuccess(response);
      }
    } catch (error) {
      toast.error(error.message || 'Invalid OTP. Please try again.');
      setOtp(['', '', '', '', '', '']); // Clear inputs on error
      otpInputs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setIsLoading(true);
    try {
      await api.post('/api/auth/resend-otp', { email });
      toast.success('OTP resent successfully!');
      setTimeLeft(600); // Reset timer
      setCanResend(false);
      setOtp(['', '', '', '', '', '']); // Clear inputs
      otpInputs.current[0]?.focus();
    } catch (error) {
      toast.error(error.message || 'Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="otp-verification-container">
      <div className="otp-card">
        <div className="otp-header">
          <h2>Verify Your Identity</h2>
          <p>Enter the 6-digit code sent to</p>
          <p className="email-display">{email}</p>
        </div>

        <form onSubmit={handleSubmit} className="otp-form">
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={el => (otpInputs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                onPaste={handlePaste}
                className="otp-input"
                disabled={isLoading}
                placeholder="0"
                data-testid={`otp-input-${index}`}
              />
            ))}
          </div>

          <div className="otp-timer">
            <span className={`timer ${timeLeft < 120 ? 'warning' : ''}`}>
              {formatTime()} remaining
            </span>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={isLoading || otp.join('').length !== 6}
            data-testid="verify-otp-button"
          >
            {isLoading ? 'Verifying...' : 'Verify Code'}
          </button>
        </form>

        <div className="otp-footer">
          <p>Didn't receive the code?</p>
          <button
            type="button"
            className="btn-link"
            onClick={handleResendOTP}
            disabled={!canResend || isLoading}
            data-testid="resend-otp-button"
          >
            {canResend ? 'Resend Code' : `Resend in ${formatTime()}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OTPVerification;
